import { useEffect, useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Landingpage from "./components/Landingpage";
import Spinner from "./components/Spinner";
import CreApp from "./cre/CreApp";
import { AUTH_SOURCE, authSourceFromSystemCode } from "./constants/authSource";
import { CRE_LOGIN_PATH } from "./constants/routes";
import { useGlobalStore } from "./context/UserContext/userContext";
import { getUserPermissions } from "./utils/API_SERVICE";
import Patient from "./pages/patient/Patient";
import VoiceTestPage from "./voice-test/VoiceCommandTestPage";
import { StudyProvider } from "./context/studyData/studyData";
import { PatientDetailsProvider } from "./context/patientDetails/patientDetails";
import {
  buildCrePatientPath,
  PATIENT_TEMPLATES_MACROS_PATH,
  shouldSkipPatientAuthRedirect,
} from "./utils/buildPatientAuthUrl";
import { completeCreMsalLogin } from "./cre/completeCreMsalLogin";
import { hasMsalRedirectInUrl } from "./cre/creRedirectUtils";

function MainApp() {
  const {
    isInitialized,
    authSource,
    initializeWithRadicalToken,
    initializeWithCreToken,
    setRadicalUser,
  } = useGlobalStore();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const hasEmbedSession =
    authSource === AUTH_SOURCE.CRE ||
    authSource === AUTH_SOURCE.RADICAL;

  useEffect(() => {
    const initializeAuth = async () => {
      const pathname = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");
      const systemCode = urlParams.get("systemCode");
      const sourceFromCode = authSourceFromSystemCode(systemCode);

      if (!token) {
        if (hasMsalRedirectInUrl()) {
          const creResult = await completeCreMsalLogin();
          if (creResult.ok) {
            if (creResult.mode === "extension") {
              navigate(creResult.extensionPath, { replace: true });
            } else {
              navigate(creResult.patientPath, { replace: true });
            }
            setIsLoading(false);
            return;
          }
        }

        const store = useGlobalStore.getState();
        if (store.authSource === AUTH_SOURCE.CRE && store.accessToken) {
          if (!shouldSkipPatientAuthRedirect(pathname)) {
            navigate(buildCrePatientPath(store.accessToken, store.creUser), {
              replace: true,
            });
          }
          setIsLoading(false);
          return;
        }

        navigate(CRE_LOGIN_PATH, { replace: true });
        setIsLoading(false);
        return;
      }

      if (
        sourceFromCode === AUTH_SOURCE.RADICAL ||
        systemCode === AUTH_SOURCE.RADICAL
      ) {
        const ok = initializeWithRadicalToken(token);
        if (ok) {
          try {
            const result = await getUserPermissions(token.trim());
            if (result?.success && result?.payload) {
              setRadicalUser(result.payload);
            }
          } catch (err) {
            console.error("GetUserPermissions failed:", err);
            setRadicalUser(null);
          }
        }
        setIsLoading(false);
        return;
      }

      if (sourceFromCode === AUTH_SOURCE.CRE) {
        const ok = await initializeWithCreToken(token);
        if (!ok) {
          useGlobalStore.getState().clear();
          navigate(CRE_LOGIN_PATH, { replace: true });
        } else if (!shouldSkipPatientAuthRedirect(pathname)) {
          const creUser = useGlobalStore.getState().creUser;
          navigate(buildCrePatientPath(token, creUser, urlParams), {
            replace: true,
          });
        }
        setIsLoading(false);
        return;
      }

      // Token without a supported systemCode (RADICAL / CRE) — send to CRE sign-in.
      useGlobalStore.getState().clear();
      navigate(CRE_LOGIN_PATH, { replace: true });
      setIsLoading(false);
    };

    initializeAuth();
  }, [
    initializeWithRadicalToken,
    initializeWithCreToken,
    setRadicalUser,
    navigate,
  ]);

  if (isLoading || (!isInitialized && !hasEmbedSession)) {
    return (
      <div className="flex items-center justify-center h-screen dark:bg-[#24262B]">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <>
      <ToastContainer autoClose={1000} />
      <StudyProvider>
        <PatientDetailsProvider>
          <Routes>
            <Route
              path={PATIENT_TEMPLATES_MACROS_PATH}
              element={<Landingpage />}
            />
            <Route path="/patient/Study/getStudyById" element={<Patient />} />
            <Route path="/voice-test" element={<VoiceTestPage />} />
          </Routes>
        </PatientDetailsProvider>
      </StudyProvider>
    </>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path={`${CRE_LOGIN_PATH}/*`} element={<CreApp />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default App;
